import React from 'react';
import ReactDOM from 'react-dom';
import { data } from 'hudl-base';
import { getConsentState } from '../../utility/consent-utils';
import { subscribeToOneTrustBannerHeight } from '../../utility/onetrust-banner-observer';
import shouldUseMobileAd from '../../utility/ads/should-use-mobile-ad';
import applyTargeting from '../../utility/ads/apply-targeting';
import './display-ad.scss';

const GOOGLE_AD_REFRESH_INTERVAL = 15000; // 15 seconds in milliseconds
const REFRESH_TIMER = 5000; // 5 seconds in milliseconds

class DisplayAd extends React.Component {
    static defaultProps = {
        isMobile: false,
        disableAdWrapper: false,
        disableSidebarOffset: false,
        mobileBreakpoint: undefined,
        variant: null,
    };

    constructor(props) {
        super(props);

        this.state = {
            isAdBlocked: false,
            lastAdRefreshTime: null,
            isActive: this.computeIsActive(props),
            bannerOffset: 0,
            sidebarLeft: 0,
        };

        this.refreshTimer = null;
        this.adContainerRef = null;
        this.unsubscribeBannerObserver = null;
        this._deferredFirstImpressionHandler = null;
        this._unmounted = false;
        this._resizeObserver = null;
    }

    getSidebarLeft = () => {
        const hudlLayout = document.querySelector('hudl-layout');
        if (!hudlLayout || !hudlLayout.shadowRoot) return 0;
        const card = hudlLayout.shadowRoot.querySelector('.global-navigation-layout-card');
        return card ? card.getBoundingClientRect().left : 0;
    };

    componentDidMount() {
        this.detectAdBlock();
        this.unsubscribeBannerObserver = subscribeToOneTrustBannerHeight(height => {
            this.setState({ bannerOffset: height });
        });
        this._resizeObserver = new ResizeObserver(() => {
            if (!this._unmounted) {
                this.setState({ sidebarLeft: this.getSidebarLeft() });
            }
        });
        const hudlLayout = document.querySelector('hudl-layout');
        if (hudlLayout) {
            this._resizeObserver.observe(hudlLayout);
        } else {
            customElements.whenDefined('hudl-layout').then(() => {
                const el = document.querySelector('hudl-layout');
                if (el && !this._unmounted) this._resizeObserver.observe(el);
            });
        }
        if (this.state.isActive) {
            this.displayOrDefer();
            if (!this.isDocumentVisible()) {
                this.startRefreshTimer();
            }
        } else {
            this.startRefreshTimer();
        }
    }

    componentDidUpdate(prevProps, prevState) {
        if (prevProps.id !== this.props.id) {
            this.displayOrDefer();
            return;
        }
        // sidebarLeft changed from 0 to a real value — the slot was created with
        // unadjusted GAM size mappings. Destroy and recreate it so the correct
        // breakpoints are applied. Skip when disableSidebarOffset is set because
        // those slots use a fixed-width container unaffected by the nav sidebar.
        if (!this.props.disableSidebarOffset && prevState.sidebarLeft === 0 && this.state.sidebarLeft > 0 && this.state.isActive) {
            const slot = this.getAdSlotByDomId(this.props.id);
            if (slot) {
                window.googletag.destroySlots([slot]);
            }
            this.displayOrDefer();
            return;
        }
        if (this.state.isActive && !prevState.isActive) {
            this.displayOrDefer();
        } else if (!this.state.isActive && prevState.isActive) {
            // Destroy the slot so it disappears from
            // `googletag.pubads().getSlots()`. Otherwise other consumers — most
            // notably the `GoogleAdRefresher` window-resize listener — will
            // refresh slots for variants that aren't currently rendered, firing
            // extra GAM calls. CBUPT-51359.
            const slot = this.getAdSlotByDomId(this.props.id);
            if (slot) {
                window.googletag.destroySlots([slot]);
            }
        }
    }

    // True when this variant matches the current viewport width. Variants
    // without a declared `variant` prop are always active (legacy callers).
    computeIsActive = (props = this.props) => {
        if (!props.variant) {
            return true;
        }
        const isNarrow = shouldUseMobileAd(props.mobileBreakpoint);
        return props.variant === 'mobile' ? isNarrow : !isNarrow;
    };

    componentWillUnmount() {
        this._unmounted = true;
        this.stopRefreshTimer();
        if (this._deferredFirstImpressionHandler) {
            document.removeEventListener('visibilitychange', this._deferredFirstImpressionHandler);
            this._deferredFirstImpressionHandler = null;
        }
        if (this.unsubscribeBannerObserver) {
            this.unsubscribeBannerObserver();
        }
        if (this._resizeObserver) {
            this._resizeObserver.disconnect();
        }
    }

    getAdSlotByDomId = (domId) => {
        const slots = window.googletag.pubads().getSlots();
        const foundSlot = slots.find(slot => slot.getSlotId().getDomId() === domId);
        return foundSlot;
    };

    detectAdBlock = () => {
        fetch('https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js', {
            method: 'HEAD',
            mode: 'no-cors',
        }).catch(() => {
            this.setState({ isAdBlocked: true });
        });
    };

    getPercentageVisible = () => {
        const node = ReactDOM.findDOMNode(this.adContainerRef);
        const rect = node.getBoundingClientRect();
        const windowHeight = window.innerHeight;

        let visibility = 0;
        if (rect.bottom > windowHeight) {
            visibility = (windowHeight - rect.top) / rect.height;
        } else if (rect.top < 0) {
            visibility = rect.bottom / rect.height;
        } else {
            visibility = 1; // Fully visible
        }
        return (visibility * 100).toFixed(0);
    };

    startRefreshTimer = () => {
        if (this.refreshTimer) {
            return;
        }
        this.refreshTimer = setInterval(() => {
            // Re-evaluate viewport-based variant selection on each tick. If the
            // active variant flipped, update state and let componentDidUpdate
            // handle (un)mounting the DOM container + firing the new request.
            // CBUPT-51359.
            const isActive = this.computeIsActive();
            if (isActive !== this.state.isActive) {
                this.setState({ isActive });
                return;
            }
            if (!isActive) {
                return;
            }

            if (!this.isDocumentVisible()) {
                return;
            }

            const percentageVisible = this.getPercentageVisible();
            const currentTime = Date.now();
            const lastRefreshTime = this.state.lastAdRefreshTime;
            const timeSinceLastRefresh = lastRefreshTime ? (currentTime - lastRefreshTime) : null;

            if (percentageVisible >= 51 && timeSinceLastRefresh >= GOOGLE_AD_REFRESH_INTERVAL) {
                googletag.cmd.push(() => {
                    if (this._unmounted) return;
                    this.refreshAdSlot();
                });
            }
        }, REFRESH_TIMER);
    };

    stopRefreshTimer = () => {
        clearInterval(this.refreshTimer);
        this.refreshTimer = null;
    };

    refreshAdSlot = () => {
        let slot = this.getAdSlotByDomId(this.props.id);
        if (!slot) {
            slot = this.createSlot(this.props.id);
            googletag.display(this.props.id);
        }
        window.googletag.pubads().refresh([slot]);
        this.setState({ lastAdRefreshTime: Date.now() });
    };

    scheduleDeferredFirstImpression = () => {
        if (this._deferredFirstImpressionHandler) {
            return; // Already pending — fires when the tab becomes visible.
        }
        this._deferredFirstImpressionHandler = () => {
            if (document.visibilityState !== 'visible') {
                return;
            }
            if (this.state.isActive) {
                this.displayAdWhenReady();
            }
            document.removeEventListener('visibilitychange', this._deferredFirstImpressionHandler);
            this._deferredFirstImpressionHandler = null;
        };
        document.addEventListener('visibilitychange', this._deferredFirstImpressionHandler);
    };

    isDocumentVisible = () => {
        return document.visibilityState === 'visible';
    };

    displayOrDefer = () => {
        if (this.isDocumentVisible()) {
            this.displayAdWhenReady();
        } else {
            this.scheduleDeferredFirstImpression();
        }
    };

    displayAdWhenReady = () => {
        getConsentState().then(() => {
            if (this._unmounted) return;
            googletag.cmd.push(() => {
                if (this._unmounted || !this.state.isActive) return; // may have flipped while consent/cmd was pending
                this.refreshAdSlot();
                this.startRefreshTimer();
            });
        });
    };

    createSlot = (adId) => {
        const googletag = window.googletag;
        const adData = data.get('ads');
        if (adData.targetingData?.nonPersonalizedAds == "True") {
            googletag.pubads().setPrivacySettings({nonPersonalizedAds: true, restrictDataProcessing: true});
        }

        if (typeof window !== 'undefined' && window.location) {
            googletag.pubads().set('page_url', window.location.origin + window.location.pathname);
        }

        const adPath = `/${adData.networkId}/${this.props.path}`;
        let slot = googletag.defineSlot(adPath, this.props.sizes, adId).setCollapseEmptyDiv(true);

        applyTargeting(slot, adData.targetingData);

        if (this.props.adSizeMapping) {
            const sidebarLeft = !this.props.disableSidebarOffset ? (this.state.sidebarLeft || 0) : 0;
            let mapping = googletag.sizeMapping();
            this.props.adSizeMapping.forEach(value => {
                const adjustedBrowser = sidebarLeft > 0
                    ? [value.browser[0] + sidebarLeft, value.browser[1]]
                    : value.browser;
                mapping = mapping.addSize(adjustedBrowser, value.ad);
            });
            slot = slot.defineSizeMapping(mapping.build());
        }

        slot = slot.addService(googletag.pubads());
        return slot;
    };


    render() {
        const { id, isMobile, disableAdWrapper } = this.props;
        const { isAdBlocked, isActive, bannerOffset, sidebarLeft } = this.state;

        // Variant doesn't match the current viewport — render nothing so the
        // ad slot stays out of the DOM and the polling timer can't refresh it.
        // CBUPT-51359.
        if (!isActive) {
            return null;
        }

        const containerStyle = isMobile ? {
            ...(bannerOffset > 0 ? { bottom: bannerOffset } : {}),
            ...(sidebarLeft > 0 ? { left: sidebarLeft } : {}),
        } : undefined;

        return disableAdWrapper || isAdBlocked ? (
            <div id={id} ref={el => (this.adContainerRef = el)} />
        ) : (
            <div className={isMobile ? 'mobile-ad-container' : ''} style={containerStyle}>
                <div id={id} ref={el => (this.adContainerRef = el)} style={!isMobile ? { width: 300, height: 250 } : {}} />
            </div>
        );
    }
}

export default DisplayAd;
